/**
 * The coupon form, as arithmetic and rules rather than JSX.
 *
 * 🔴 CHANGE 19 §16 CHANGED THE SHAPE OF THIS FORM, AND THE THREE CHANGES ARE
 * EACH A WAY THE OLD ONE WAS WRONG:
 *
 *   1. **A discount per child.** Every plan in this product is priced per child
 *      (`Plan.perStudentPaise`), so a concession is negotiated the same way —
 *      *"₹10 a head off for the pilot term"*. The old form could only say "20%"
 *      or "₹5,000 off in total", and the second has to be recomputed by hand
 *      every time the school enrols another class.
 *   2. **An expiry in days.** Nobody agrees a discount "until 12 November"; they
 *      agree it for a term or a fortnight. A date picker made the operator do
 *      calendar arithmetic on a phone call and get it wrong on the 31st.
 *   3. **A school picked from a list, and optional.** The old field was a free
 *      TEXT BOX labelled *"Search school name"* whose value was sent as
 *      `schoolId`. It searched nothing. Anything typed into it produced a 404,
 *      and anything left in it by accident bound the coupon to a school that
 *      does not exist — a code that refuses everybody, forever, for a reason no
 *      screen shows.
 *
 * 🔴 EVERY AMOUNT IS A DECIMAL STRING ON THE WIRE AND AN INTEGER BELOW IT — the
 * rule `billing.ts` and `discountModel.ts` both keep, for the reason
 * `Number('19.99') * 100 === 1998.9999999999998` makes on its own.
 */

export type CouponKindChoice = "PERCENT" | "AMOUNT" | "PER_STUDENT";

/** Mirrors `amountToPaise` on the server. */
const MONEY = /^\d+(?:\.\d{1,2})?$/;
/** Mirrors `couponCode` in `billing.dto.ts`. */
const CODE = /^[A-Z0-9-]+$/;

export const COUPON_CODE_MAX = 40;
export const COUPON_NAME_MAX = 80;
/** The server's own ceiling on `expiresAt` arithmetic is a date; this is ours. */
export const EXPIRES_IN_DAYS_MAX = 3650;

export interface CouponDraft {
  code: string;
  name: string;
  kind: CouponKindChoice;
  /** Whole percent, as typed. Read only when `kind` is `PERCENT`. */
  percent: string;
  /** Rupees, as typed. Read for both flat kinds — `kind` says which. */
  amount: string;
  /** How many days from today the code stops working. Empty means never. */
  expiresInDays: string;
  /** Billing periods the discount lasts. Empty means every period. */
  durationPeriods: string;
  /** Total claims allowed. Empty means uncapped. */
  maxRedemptions: string;
  /** 🔴 A school ID FROM THE PICKER, or empty for "any school". Never typed. */
  schoolId: string;
  newSchoolsOnly: boolean;
}

export interface CouponDraftErrors {
  code: string | null;
  name: string | null;
  value: string | null;
  expiresInDays: string | null;
  durationPeriods: string | null;
  maxRedemptions: string | null;
}

/** An optional whole number: blank is a real answer, and "abc" is not. */
const optionalCount = (
  raw: string,
  field: string,
  max: number,
): string | null => {
  const value = raw.trim();
  if (value === "") return null;
  if (!/^\d+$/.test(value)) return `${field} is a whole number, or leave it empty.`;
  const n = Number(value);
  if (n < 1) return `${field} is at least 1, or leave it empty.`;
  if (n > max) return `${field} is at most ${String(max)}.`;
  return null;
};

export function validateCouponDraft(draft: CouponDraft): CouponDraftErrors {
  const code = draft.code.trim().toUpperCase();
  const name = draft.name.trim();

  let value: string | null = null;
  if (draft.kind === "PERCENT") {
    const pct = draft.percent.trim();
    if (pct === "") value = "How much off? Type a whole percentage.";
    else if (!/^\d+$/.test(pct)) value = "A percentage is a whole number, like 20.";
    else if (Number(pct) < 1 || Number(pct) > 100) value = "A percentage is between 1 and 100.";
  } else {
    const amt = draft.amount.trim();
    const what = draft.kind === "PER_STUDENT" ? "the amount off per child" : "the amount off";
    if (amt === "") value = `How much off? Type ${what} in rupees.`;
    else if (!MONEY.test(amt))
      value = `${what} is digits with at most two decimals, like 10.50. No ₹ sign, no commas.`;
    else if (Number(amt) === 0) value = "A discount of zero takes nothing off.";
  }

  return {
    code:
      code === ""
        ? "A coupon needs a code — this is what a school types."
        : !CODE.test(code)
          ? "Capital letters, digits and hyphens only, like PILOT-2026."
          : code.length > COUPON_CODE_MAX
            ? `A code is at most ${String(COUPON_CODE_MAX)} characters.`
            : null,
    name:
      name === ""
        ? "A coupon needs a name — this is what you will recognise it by."
        : name.length > COUPON_NAME_MAX
          ? `A name is at most ${String(COUPON_NAME_MAX)} characters.`
          : null,
    value,
    expiresInDays: optionalCount(draft.expiresInDays, "The expiry", EXPIRES_IN_DAYS_MAX),
    durationPeriods: optionalCount(draft.durationPeriods, "The duration", 120),
    maxRedemptions: optionalCount(draft.maxRedemptions, "The claim limit", 100_000),
  };
}

export const couponDraftIsValid = (draft: CouponDraft): boolean =>
  Object.values(validateCouponDraft(draft)).every((m) => m === null);

/**
 * "Expires in 30 days" as the instant the code stops working.
 *
 * 🔴 IT ADDS DAYS TO A **UTC MIDNIGHT**, NOT TO `now`. `couponRefusal` on the
 * server refuses a coupon once `now >= expiresAt`, so an expiry stamped at the
 * moment of creation would cut the last day short by however long the operator
 * spent on the phone. Midnight at the end of the last day is what "for thirty
 * days" means to the person being told it.
 *
 * ⚠️ UTC RATHER THAN LOCAL, and the cost is named: an operator in IST creating a
 * coupon at 11pm sees it expire 5½ hours "early" by their clock on the final
 * day. The alternative is an expiry that depends on which timezone the browser
 * was in, which is worse — the server compares against a UTC instant and the
 * screen shows the date this returns, so the two always agree.
 */
export function expiryFromDays(days: string, now: Date): string | null {
  const value = days.trim();
  if (value === "" || !/^\d+$/.test(value)) return null;
  const n = Number(value);
  if (n < 1 || n > EXPIRES_IN_DAYS_MAX) return null;

  const midnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return new Date(midnight + n * 86_400_000).toISOString();
}

/**
 * What a school's row says about it in the picker.
 *
 * 🔴 CHANGE 19 §16 ASKS FOR TRIAL AND ON-PLAN SCHOOLS TO BE PICKABLE, AND THE
 * DISTINCTION HAS TO BE VISIBLE RATHER THAN IMPLIED. Attaching a discount to a
 * school still on a free trial is a real thing to do — it is what the trial
 * converts onto — but it is a different decision from discounting a school that
 * is already paying, and an operator picking from a flat list of names cannot
 * tell which one they just chose.
 */
export type SchoolBillingState = "TRIAL" | "ON_PLAN" | "NO_PLAN";

export interface PickableSchool {
  id: string;
  name: string;
  state: SchoolBillingState;
  planName: string | null;
}

export function pickableSchools(
  rows: readonly {
    schoolId: string;
    schoolName: string;
    status: string;
    plan: { name: string } | null;
  }[],
): PickableSchool[] {
  return rows
    .map((row) => ({
      id: row.schoolId,
      name: row.schoolName,
      state: (row.status === "TRIAL"
        ? "TRIAL"
        : row.plan === null
          ? "NO_PLAN"
          : "ON_PLAN") as SchoolBillingState,
      planName: row.plan?.name ?? null,
    }))
    /* Trials first: they are the schools a discount is usually being written
       for, and a list of two hundred names buries them otherwise. */
    .sort(
      (a, b) =>
        SCHOOL_ORDER[a.state] - SCHOOL_ORDER[b.state] || a.name.localeCompare(b.name),
    );
}

const SCHOOL_ORDER: Record<SchoolBillingState, number> = {
  TRIAL: 0,
  ON_PLAN: 1,
  NO_PLAN: 2,
};

/** The label beside a school's name in the picker. */
export function schoolStateLabel(school: PickableSchool): string {
  if (school.state === "TRIAL") return "free trial";
  if (school.state === "ON_PLAN") return school.planName ?? "on a plan";
  return "no plan yet";
}

/**
 * What the operator is about to create, in one sentence.
 *
 * 🔴 IT NAMES "PER CHILD" WHENEVER THAT IS WHAT IT IS. The two flat kinds share
 * a column, a form field and a rendered figure; they differ by a factor of the
 * school's whole enrolment. This sentence is the last place before the write
 * where the difference is visible.
 */
export function describeCouponDraft(draft: CouponDraft, expiresAt: string | null): string {
  const off =
    draft.kind === "PERCENT"
      ? `${draft.percent.trim()}% off`
      : draft.kind === "PER_STUDENT"
        ? `₹${draft.amount.trim()} off per child`
        : `₹${draft.amount.trim()} off in total`;

  const when =
    expiresAt === null
      ? "It never expires"
      : `It stops working on ${new Date(expiresAt).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        })}`;

  const scope = draft.schoolId === "" ? "any school that types it" : "one named school";

  return `${off}, for ${scope}. ${when}.`;
}
